import { errorDetail } from '../../features/events/client';
import { apiEndpoints, apiRequest } from '../../utils/apiService';

/**
 * Hotel dashboard API (`/api/hospitality/`, W9). Every call names its
 * organization with X-Store-Group-ID and sends no X-Store-ID: hotels hang off
 * the store group, not a store. Shapes mirror the W9 serializers; none of
 * them carries a PMS id, and stays are addressed by `stay_ref` only.
 */

export type DisplayStatus =
  | 'on_hold'
  | 'confirmed'
  | 'hold_expired'
  | 'cancelled'
  | 'in_house'
  | 'checked_out'
  | 'inquiry';

export type StaySource = 'voice' | 'dashboard' | 'sync';

export type Stay = {
  stay_ref: string;
  confirmation_number: string;
  state: string;
  display_status: DisplayStatus;
  source: StaySource;
  guest_first_name: string;
  guest_last_name: string;
  room_type_name: string;
  rate_plan_name: string;
  room_number: string;
  /** The property's local dates (ISO). */
  arrival_date: string;
  departure_date: string;
  nights: number;
  start_utc: string;
  end_utc: string;
  adult_count: number;
  child_count: number;
  total_amount: string | null;
  currency: string;
  hold_release_utc: string | null;
  hold_expired_at: string | null;
  created_at: string;
  updated_at: string;
};

export type StayAction = {
  action: string;
  status: 'pending' | 'succeeded' | 'failed' | 'unknown';
  error_code: string;
  created_at: string;
  finished_at: string | null;
};

export type StayDetail = Stay & {
  guest_email: string;
  guest_phone_e164: string;
  last_synced_at: string | null;
  actions: StayAction[];
};

export type Page<T> = {
  results: T[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
};

export type StayFilters = {
  status?: DisplayStatus;
  q?: string;
  arrival_from?: string;
  arrival_to?: string;
  page?: number;
};

export type PropertySummary = {
  id: number;
  name: string;
  time_zone: string;
  currency: string;
  voice_booking_enabled: boolean;
};

/** A store group a platform admin may open (`/api/stores/store-groups/`). */
export type StoreGroupOption = { id: number; name: string };

export type GuaranteeMode = 'none' | 'mews_payment_request' | 'stripe_link';

/** What the hotel may change. Everything else in the settings is read-only. */
export type PropertySettingsInput = {
  voice_booking_enabled: boolean;
  guarantee_mode: GuaranteeMode;
  hold_minutes: number;
  front_desk_phone: string;
  group_room_threshold: number;
  booking_url: string;
  check_in_time: string | null;
  check_out_time: string | null;
};

export type PropertySettings = PropertySettingsInput & {
  id: number;
  name: string;
  time_zone: string;
  pricing_mode: 'gross' | 'net';
  currency: string;
  voice_booking_blockers: string[];
  created_at: string;
  updated_at: string;
};

export type Onboarding = {
  property_id: number;
  connection: null | {
    provider: string;
    provider_name: string;
    environment: 'live' | 'sandbox';
    status: 'pending' | 'active' | 'inactive' | 'failed';
    has_credential: boolean;
    credential_rotated_at: string | null;
    last_synced_at: string | null;
  };
  last_sync: null | {
    kind: string;
    status: string;
    started_at: string | null;
    completed_at: string | null;
    items_failed: number;
  };
  lines: { phone_number: string; is_active: boolean }[];
  voice_booking_enabled: boolean;
  voice_booking_blockers: string[];
};

/** The access token is write-only: it is sent once and never read back. */
export type OnboardingInput = {
  provider?: string;
  environment?: 'live' | 'sandbox';
  access_token?: string;
};

export class HotelApiError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly detail: string | null = null,
    /** DRF field errors, `{field: [messages]}`. */
    readonly fields: Record<string, string[]> = {},
  ) {
    super(detail ?? message);
    this.name = 'HotelApiError';
  }
}

const baseURL = process.env.REACT_APP_API_BASE_URL;

function fieldErrors(body: unknown): Record<string, string[]> {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return {};
  const out: Record<string, string[]> = {};
  for (const [name, value] of Object.entries(body)) {
    if (Array.isArray(value)) out[name] = value.map(String);
  }
  return out;
}

async function hotelJson<T>(
  organizationId: number,
  path: string,
  label: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const response = await apiRequest(`${baseURL}/api/hospitality${path}`, {
    method: init.method ?? 'GET',
    storeGroupId: organizationId,
    storeId: null,
    ...(init.body !== undefined && { body: JSON.stringify(init.body) }),
  });
  if (!response) throw new HotelApiError(`${label}: not signed in`, null);
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    throw new HotelApiError(
      `${label} failed (${response.status})`,
      response.status,
      errorDetail(body),
      fieldErrors(body),
    );
  }
  return (await response.json()) as T;
}

export function stayQuery(filters: StayFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Every group for a platform admin; the API lists all of them, unpaginated. */
async function storeGroups(): Promise<StoreGroupOption[]> {
  const response = await apiRequest(apiEndpoints.getStoreGroups(), {
    method: 'GET',
    storeId: null,
  });
  if (!response) throw new HotelApiError('Store groups: not signed in', null);
  if (!response.ok) {
    throw new HotelApiError(
      `Store groups failed (${response.status})`,
      response.status,
    );
  }
  const body = (await response.json()) as
    | StoreGroupOption[]
    | { results?: StoreGroupOption[] };
  const list = Array.isArray(body) ? body : (body.results ?? []);
  return list.map(({ id, name }) => ({ id, name }));
}

const stayPath = (propertyId: number, stayRef: string) =>
  `/properties/${propertyId}/stays/${encodeURIComponent(stayRef)}/`;

export const hotelApi = {
  properties: (org: number) =>
    hotelJson<PropertySummary[]>(org, '/properties/', 'Properties'),
  settings: (org: number, propertyId: number) =>
    hotelJson<PropertySettings>(
      org,
      `/properties/${propertyId}/`,
      'Property settings',
    ),
  saveSettings: (
    org: number,
    propertyId: number,
    values: Partial<PropertySettingsInput>,
  ) =>
    hotelJson<PropertySettings>(
      org,
      `/properties/${propertyId}/`,
      'Save settings',
      { method: 'PATCH', body: values },
    ),
  stays: (org: number, propertyId: number, filters: StayFilters) =>
    hotelJson<Page<Stay>>(
      org,
      `/properties/${propertyId}/stays/${stayQuery(filters)}`,
      'Stays',
    ),
  stay: (org: number, propertyId: number, stayRef: string) =>
    hotelJson<StayDetail>(org, stayPath(propertyId, stayRef), 'Stay'),
  onboarding: (org: number, propertyId: number) =>
    hotelJson<Onboarding>(
      org,
      `/properties/${propertyId}/onboarding/`,
      'PMS connection',
    ),
  saveOnboarding: (org: number, propertyId: number, values: OnboardingInput) =>
    hotelJson<Onboarding>(
      org,
      `/properties/${propertyId}/onboarding/`,
      'Save PMS connection',
      { method: 'PATCH', body: values },
    ),
  storeGroups,
};
